import React, { useEffect, useRef, useState } from 'react';
import { CheckCircleFill, Compass } from 'react-bootstrap-icons';
import { useLocale } from '../../i18n/LocaleContext';
import { STYLE_CATEGORIES, resolveStyleCategory, updateBrandStyleCategory } from '../../api/styleCategory';

// Datasheet: "Style category" — which Style Universe galaxy the brand's store
// stands in on the map. One choice out of the 11 galaxies, saved the moment
// it is picked (optimistic, rolled back when the save fails — the same
// behaviour as the logo-shape picker next to it). Read-only without the
// editBrand permission. No category yet → a gentle prompt to pick one.
// No emoji (CEO rule): each galaxy is marked by its colour only — the left
// edge and a dot.
//
// `detail`: the GET /brands/:id answer the section already holds (null while
// it loads); `brand`: the session brand.
export default function StyleCategoryPicker({ brand, detail, canEdit }) {
  const { t } = useLocale();
  // `pending` is the optimistic choice; undefined = show the stored one.
  const [pending, setPending] = useState(undefined);
  const [status, setStatus] = useState('idle'); // idle | saving | saved | error
  const seq = useRef(0);
  // What the last successful save stored — newer than both payloads.
  const saved = useRef(undefined);

  useEffect(() => {
    seq.current += 1;
    saved.current = undefined;
    setPending(undefined);
    setStatus('idle');
  }, [brand.id]);

  const stored = saved.current !== undefined ? saved.current : resolveStyleCategory(brand, detail);
  const value = pending !== undefined ? pending : stored;
  const current = STYLE_CATEGORIES.find((c) => c.key === value) || null;
  const nameOf = (key) => t(`styleCategory.name.${key}`);

  const choose = async (key) => {
    if (!canEdit || key === value) return;
    const mine = ++seq.current;
    const rollbackTo = stored;
    setPending(key);
    setStatus('saving');
    try {
      const result = await updateBrandStyleCategory(brand.id, key);
      if (mine !== seq.current) return;
      saved.current = result;
      setPending(undefined);
      setStatus('saved');
    } catch {
      if (mine !== seq.current) return;
      saved.current = rollbackTo;
      setPending(undefined);
      setStatus('error');
    }
  };

  const note =
    !canEdit ? (current ? t('styleCategory.readOnly') : t('styleCategory.readOnlyNone'))
      : status === 'saving' ? t('styleCategory.saving')
        : status === 'saved' && current ? t('styleCategory.saved', { galaxy: nameOf(current.key) })
          : status === 'error' ? t('styleCategory.error')
            : null;

  return (
    <div className={`bb-style${!current ? ' is-empty' : ''}`}>
      <div className="bb-style__head">
        <span className="bb-style__icon" aria-hidden="true"><Compass size={16} /></span>
        <div className="bb-style__text">
          <h3 className="bb-style__label" id="bb-style-label">
            {t('styleCategory.label')}
            {current && (
              <span className="bb-style__current" style={{ '--gx': current.color }}>
                <span className="bb-style__dot" aria-hidden="true" />
                {nameOf(current.key)}
              </span>
            )}
          </h3>
          <p className="bb-style__lead" id="bb-style-lead">{t('styleCategory.explainer')}</p>
        </div>
      </div>

      {!current && canEdit && (
        <p className="bb-style__prompt">{t('styleCategory.prompt')}</p>
      )}

      <div
        className="bb-style__grid"
        role="radiogroup"
        aria-labelledby="bb-style-label"
        aria-describedby="bb-style-lead"
      >
        {STYLE_CATEGORIES.map((c) => {
          const on = value === c.key;
          return (
            <button
              key={c.key}
              type="button"
              role="radio"
              aria-checked={on}
              className={`bb-style__chip${on ? ' is-on' : ''}`}
              style={{ '--gx': c.color }}
              disabled={!canEdit}
              onClick={() => choose(c.key)}
            >
              <span className="bb-style__dot" aria-hidden="true" />
              <span className="bb-style__name">{nameOf(c.key)}</span>
              {on && <CheckCircleFill className="bb-style__check" size={14} aria-hidden="true" />}
            </button>
          );
        })}
      </div>

      <span
        className={`bb-style__note${status === 'error' && canEdit ? ' is-error' : ''}`}
        role={status === 'error' ? 'alert' : undefined}
        aria-live="polite"
      >
        {note}
      </span>
    </div>
  );
}
